import React, { useEffect, useState } from 'react';
import { Modal } from '../../../components/organisms/Modal';
import { Button } from '../../../components/atoms';
import { FormField } from '../../../components/molecues/FormField';
import type {
  Album,
  AlbumContentRegion,
  CreateAlbumRequest,
  UpdateAlbumRequest,
} from '../../../types/api/album';

interface AlbumFormState {
  name: string;
  albumType: AlbumContentRegion;
  priority: string;
  releaseDate: string;
  description: string;
}

const defaultFormState: AlbumFormState = {
  name: '',
  albumType: 'myanmar',
  priority: '0',
  releaseDate: '',
  description: '',
};

const PRIORITY_MIN = 0;
const PRIORITY_MAX = 999;

export interface AlbumFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateAlbumRequest | UpdateAlbumRequest,
    coverImageFile?: File | null,
    imageFile?: File | null
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  album?: Album;
}

export const AlbumFormModal: React.FC<AlbumFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  album,
}) => {
  const [formData, setFormData] = useState<AlbumFormState>(defaultFormState);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && album && isOpen) {
      setFormData({
        name: album.name || '',
        albumType: album.albumType || 'myanmar',
        priority:
          album.priority !== undefined && album.priority !== null
            ? String(album.priority)
            : '0',
        releaseDate: album.releaseDate ? album.releaseDate.substring(0, 10) : '',
        description: album.description || '',
      });
      setCoverImagePreview(album.coverArtUrl || null);
      setImagePreview(album.imageUrl || null);
      setCoverImageFile(null);
      setImageFile(null);
    } else if (isOpen) {
      setFormData(defaultFormState);
      setCoverImagePreview(null);
      setImagePreview(null);
      setCoverImageFile(null);
      setImageFile(null);
    }
    setErrors({});
  }, [album, mode, isOpen]);

  const validateImage = (file: File): string | null => {
    if (!file.type.startsWith('image/')) {
      return 'Please select a valid image file';
    }
    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) {
      return `Image size must be less than ${maxSizeMb}MB`;
    }
    return null;
  };

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: 'coverImage' | 'image'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImage(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, [field]: validationError }));
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });

    const reader = new FileReader();
    reader.onload = () => {
      if (field === 'coverImage') {
        setCoverImageFile(file);
        setCoverImagePreview(reader.result as string);
      } else {
        setImageFile(file);
        setImagePreview(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      nextErrors.name = 'Album name is required';
    } else if (formData.name.trim().length > 200) {
      nextErrors.name = 'Album name must be less than 200 characters';
    }
    if (!formData.albumType) {
      nextErrors.albumType = 'Album type is required';
    }

    const trimmedPriority = formData.priority.trim();
    if (trimmedPriority === '') {
      nextErrors.priority = 'Priority is required';
    } else {
      const priorityNumber = Number(trimmedPriority);
      if (!Number.isFinite(priorityNumber) || !Number.isInteger(priorityNumber)) {
        nextErrors.priority = 'Priority must be a whole number';
      } else if (priorityNumber < PRIORITY_MIN || priorityNumber > PRIORITY_MAX) {
        nextErrors.priority = `Priority must be between ${PRIORITY_MIN} and ${PRIORITY_MAX}`;
      }
    }

    if (formData.description.length > 1000) {
      nextErrors.description = 'Description must be less than 1000 characters';
    }

    if (formData.releaseDate) {
      const parsedDate = new Date(formData.releaseDate);
      if (Number.isNaN(parsedDate.getTime())) {
        nextErrors.releaseDate = 'Invalid release date';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    const payload: CreateAlbumRequest | UpdateAlbumRequest = {
      name: formData.name.trim(),
      albumType: formData.albumType,
      priority: Number(formData.priority.trim()),
      releaseDate: formData.releaseDate || null,
      description: formData.description.trim() || null,
    };

    setIsSubmitting(true);
    try {
      await onSubmit(payload, coverImageFile, imageFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Add Album' : 'Edit Album'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isSubmitting} type="submit">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label="Album Name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={errors.name}
          required
          placeholder="e.g., Sample Album"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
            Album Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.albumType}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                albumType: e.target.value as AlbumContentRegion,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
          >
            <option value="myanmar">Myanmar</option>
            <option value="international">International</option>
          </select>
          {errors.albumType ? (
            <p className="text-red-500 text-xs mt-1">{errors.albumType}</p>
          ) : null}
        </div>

        <FormField
          label="Priority"
          type="number"
          value={formData.priority}
          onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
          error={errors.priority}
          required
          min={PRIORITY_MIN}
          max={PRIORITY_MAX}
          step={1}
          helperText={`Lower number shows first (${PRIORITY_MIN}\u2013${PRIORITY_MAX})`}
          placeholder="0"
        />

        <FormField
          label="Release Date"
          type="date"
          value={formData.releaseDate}
          onChange={(e) => setFormData((prev) => ({ ...prev, releaseDate: e.target.value }))}
          error={errors.releaseDate}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Description</label>
          <textarea
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            placeholder="Optional album description"
          />
          {errors.description ? <p className="text-red-500 text-xs mt-1">{errors.description}</p> : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">Cover Image</label>
            <div className="w-full h-28 rounded bg-gray-100 border flex items-center justify-center overflow-hidden mb-2">
              {coverImagePreview ? (
                <img src={coverImagePreview} alt="Cover preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-gray-400">No cover image</span>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, 'coverImage')}
              className="block text-sm text-gray-700"
            />
            {errors.coverImage ? <p className="text-sm text-red-600 mt-1">{errors.coverImage}</p> : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">Image</label>
            <div className="w-full h-28 rounded bg-gray-100 border flex items-center justify-center overflow-hidden mb-2">
              {imagePreview ? (
                <img src={imagePreview} alt="Image preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-gray-400">No image</span>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, 'image')}
              className="block text-sm text-gray-700"
            />
            {errors.image ? <p className="text-sm text-red-600 mt-1">{errors.image}</p> : null}
          </div>
        </div>
      </form>
    </Modal>
  );
};
